import React, { useState, useEffect, useRef } from "react";
import Rating from "@mui/material/Rating";
import {
  JobberProfileBuilder,
  Email,
  JobberOrgRatingClientResp,
  JobberPerformanceOrg,
  CorpDataObj,
  OrgMetaData,
} from "../orgViews/orgConstants";
import { CorpDataObjSimpleFromLocalStorage, LocalStorageObjects, OrgLocation, OrgLocationMapDetails, JobberStatus } from "../common/CommonInterfaces";
import { UserType } from "../remoteExecution/RemoteExecutionServiceImpl";
import { v4 as uuidv4 } from 'uuid';
import { Trophy } from "lucide-react";
import { ca } from "date-fns/locale";


enum LOCAL_STORAGE_KEYS {
  WORKY_SESSION_ID = "worky_session_id",
  SUB_SESSION_WORKY_ID = "sub_session_worky_id",
  AUTH_CODE = "auth_code",
  AUTH_FLOW = "auth_flow",
  PENDING_MFA_EMAIL = "pending_mfa_email",
  PENDING_MFA_CHALLENGE_STEP = "pending_mfa_challenge_step",
  PENDING_MFA_PRIMARY_WORKY_CORP_ID = "pending_mfa_primary_worky_corp_id",
  PENDING_NEW_PASSWORD_EMAIL = "pending_new_password_email",
  PENDING_NEW_PASSWORD_PRIMARY_WORKY_CORP_ID = "pending_new_password_primary_worky_corp_id",
  LOCATION_PERMISSION = "locationPermission",
  ACCESS_TOKEN = "access_token",
  ID_TOKEN = "id_token",
  REFRESH_TOKEN = "refresh_token",
  EXPIRE_TIME = "expire_time",
  USER_EMAIL = "userEmail",
  USER_PHONE_NUMBER = "userPhoneNumber",
  FIRST_NAME = "firstName",
  LAST_NAME = "lastName",
  USER_ID = "userId",
  ORG_NAME = "orgName",
  ORG_ICON = "orgIcon",
  USER_TYPE = "user_type",
  APP_LOAD_COUNT = "app_load_count",
  POP_UP_DIRECT_PITCH_COUNT = "pop_up_direct_pitch_count",
  ORG_DATA = "orgData",
  GANTT_GROUP_BY = "gantt_group_by",
  GANTT_VIEW_ALL = "gantt_view_all",
  GANTT_CURRENT_DATE = "gantt_current_date",
  WORKY_NAVIGATION_COLLAPSED = "workyNavigationCollapsed",
  WORKY_NAVIGATION_EXECUTION_EXPANDED = "workyNavigationExecutionExpanded",
  WORKY_NAVIGATION_FINANCE_EXPANDED = "workyNavigationFinanceExpanded",
  WORKY_NAVIGATION_SALES_EXPANDED = "workyNavigationSalesExpanded",
  COOKIE_CONSENT_STATUS = "worky_cookie_consent_status",
  LANDING_INCENTIVE_BANNER_DISMISSED = "landing_incentive_banner_dismissed",
  AGENCY_CLIENT_INVOICES_VIEW_MODE = "agency_client_invoices_view_mode",
  AGENCY_CLIENT_LIST_VIEW = "agency_client_list_view"
}




const CopyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    console.log("Copied to clipboard: " + value);
  } catch (err) {
    console.error("Unable to copy to clipboard:", err);
  }
};

function extractRatingFromRatingCategory(ratingString){
  console.log("rating value" + ratingString)
  switch (ratingString) {
      case "DEFAULT_WAITING_FOR_REVIEW":
        return 0;
      case "OUTSTANDING":
        return 5;
      case "EXCEEDS_EXPECTATION":
        return 4;
      case "MEET_EXPECTATION":
        return 3;
      case "UNSATISFACTORY":
        return 2;
      default:
        return 4;
    }
}


const ExtractStarRating = (jobberRating: Map<Email, JobberPerformanceOrg>) => {
  if (jobberRating) {
    const orgEmail = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_EMAIL});
    const emailMapKey = `EmailAddress(value=${orgEmail})`;
    const jobberRatingEnum =
      jobberRating[emailMapKey]?.jobberPerformanceOrgDetails?.category;

    switch (jobberRatingEnum) {
      case "DEFAULT_WAITING_FOR_REVIEW":
        return 0;
      case "OUTSTANDING":
        return 5;
      case "EXCEEDS_EXPECTATION":
        return 4;
      case "MEET_EXPECTATION":
        return 3;
      case "UNSATISFACTORY":
        return 2;
      default:
        return 4;
    }
  }

  return 4;
};

const JobberStarRating = ({
  jobberRating,
}: {
  jobberRating: JobberProfileBuilder;
}) => {
  const starValue = ExtractStarRating(jobberRating.jobberOrgRatingDetailMap);
  if (jobberRating) {
    return (
      <div className="jobber-profile-modal-stars-holder">
        <Rating
          name="half-rating-read"
          defaultValue={starValue}
          precision={0.5}
          readOnly
        />
      </div>
    );
  }
};

const JobberViewRating = ({
    jobberRating,
  }: {
    jobberRating: JobberProfileBuilder;
  }) => {
  // Component to extract reviewer name
  const ViewRatingBuilderV2 = () => {
    // const jobberRating = responseData.data; // Assuming responseData is the name of your response object
    const jobberOrgRatingDetailMap = jobberRating.jobberOrgRatingDetailMap;

    return (
      <>
        {Object.keys(jobberOrgRatingDetailMap).map((emailKey) => {
          const performanceDetails =
            jobberOrgRatingDetailMap[emailKey].jobberPerformanceOrgDetails;

          return (
            <div key={emailKey}>
              <div className="org-rating">
                <div className="org-rating-name">
                  <ExtractedReviewerName
                    firstName={performanceDetails.requestFirstName}
                    lastName={performanceDetails.requestLastName}
                  />
                </div>
                <div className="org-rating-value">
                  <div className="org-rating-value-star">
                    <ExtractedReviewerStarRating
                      category={performanceDetails.category}
                    />
                  </div>
                  <div className="org-rating-value-text">
                    <ExtractedReviewerReason
                      reason={performanceDetails.reason}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  };
  const ExtractedReviewerName = ({ firstName, lastName }) => {
    return (
      <>
        {firstName} {lastName}
      </>
    );
  };

  // Component to extract reviewer star rating
  const ExtractedReviewerStarRating = ({ category }) => {
    switch (category) {
      case "DEFAULT_WAITING_FOR_REVIEW":
        return (
          <Rating
            size="small"
            name="half-rating-read"
            defaultValue={0}
            precision={0.5}
            readOnly
          />
        );
      case "OUTSTANDING":
        return (
          <Rating
            size="small"
            name="half-rating-read"
            defaultValue={5}
            precision={0.5}
            readOnly
          />
        );
      case "EXCEEDS_EXPECTATION":
        return (
          <Rating
            size="small"
            name="half-rating-read"
            defaultValue={4}
            precision={0.5}
            readOnly
          />
        );
      case "MEET_EXPECTATION":
        return (
          <Rating
            size="small"
            name="half-rating-read"
            defaultValue={3}
            precision={0.5}
            readOnly
          />
        );
      case "UNSATISFACTORY":
        return (
          <Rating
            size="small"
            name="half-rating-read"
            defaultValue={2}
            precision={0.5}
            readOnly
          />
        );
      default:
        return (
          <Rating
            size="small"
            name="half-rating-read"
            defaultValue={0}
            precision={0.5}
            readOnly
          />
        );
    }
  };

  // Component to extract reviewer reason
  const ExtractedReviewerReason = ({ reason }) => {
    return <>{reason}</>;
  };

  const JobberViewRating = ({
    jobberRating,
  }: {
    jobberRating: JobberProfileBuilder;
  }) => {
    // Component to extract reviewer name
    const ExtractedReviewerName = ({ firstName, lastName }) => {
      return (
        <>
          {firstName} {lastName}
        </>
      );
    };

    // Component to extract reviewer star rating
    const ExtractedReviewerStarRating = ({ category }) => {
      switch (category) {
        case "DEFAULT_WAITING_FOR_REVIEW":
          return (
            <Rating
              size="small"
              name="half-rating-read"
              defaultValue={0}
              precision={0.5}
              readOnly
            />
          );
        case "OUTSTANDING":
          return (
            <Rating
              size="small"
              name="half-rating-read"
              defaultValue={5}
              precision={0.5}
              readOnly
            />
          );
        case "EXCEEDS_EXPECTATION":
          return (
            <Rating
              size="small"
              name="half-rating-read"
              defaultValue={4}
              precision={0.5}
              readOnly
            />
          );
        case "MEET_EXPECTATION":
          return (
            <Rating
              size="small"
              name="half-rating-read"
              defaultValue={3}
              precision={0.5}
              readOnly
            />
          );
        case "UNSATISFACTORY":
          return (
            <Rating
              size="small"
              name="half-rating-read"
              defaultValue={2}
              precision={0.5}
              readOnly
            />
          );
        default:
          return (
            <Rating
              size="small"
              name="half-rating-read"
              defaultValue={0}
              precision={0.5}
              readOnly
            />
          );
      }
    };

    // Component to extract reviewer reason
    const ExtractedReviewerReason = ({ reason }) => {
      return <>{reason}</>;
    };

    

    return (
      <div className="jobber-profile-view-ratings">
        <div className="jobber-profile-view-ratings-title">Ratings</div>
        <ViewRatingBuilderV2 />
      </div>
    );
  };

  return (
    <div className="jobber-profile-view-ratings">
      <div className="jobber-profile-view-ratings-title">Ratings</div>
      <ViewRatingBuilderV2 />
    </div>
  );
};


const DetermineUserType = (userTypeString: string): UserType | null => {
  if (userTypeString === UserType.JOBBER) {
    return UserType.JOBBER;
  } else if (userTypeString === UserType.ORG_MANAGER) {
    return UserType.ORG_MANAGER;
  } else if (userTypeString === UserType.DIRECT_CARE_PATIENT) {
    return UserType.DIRECT_CARE_PATIENT;
  } else if (userTypeString === UserType.DIRECT_KIN_CARE_GIVER) {
    return UserType.DIRECT_KIN_CARE_GIVER;
  } else if (userTypeString === UserType.AGENCY_STAFF) {
    return UserType.AGENCY_STAFF;
  } else if (userTypeString === UserType.AGENCY_ADMIN) {
    return UserType.AGENCY_ADMIN;
  } else if (userTypeString === UserType.WORKY_STAFF) {
    return UserType.WORKY_STAFF;
  } else if (userTypeString === UserType.WORKY_ADMIN) {
    return UserType.WORKY_ADMIN;
  } else if (userTypeString === UserType.AGENCY_STAFF_SCOPED) {
    return UserType.AGENCY_STAFF_SCOPED;
  }
  return null;
};

/**
 * 
 * @param epochSeconds epoch time in seconds
 * @returns string in the format of "YYYY-MM-DD HH:MM:SS"
 */
function convertEpochToHumanFriendlyDate(epochSeconds: number): string {
  const date = new Date(epochSeconds * 1000);
  
  // Pad with leading zeros where necessary
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * @param epochSeconds epoch time in seconds
 * @returns UTC calendar date only as "YYYY-MM-DD" (no time component)
 */
const convertEpochToDateOnlyString = (epochSeconds: number): string => {
  try{
    if (!isValidNumber(epochSeconds)) {
      return "";
    }
    const date: Date = new Date(epochSeconds * 1000);
    const year: number = date.getUTCFullYear();
    const month: string = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day: string = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }catch(e){
    console.error("Error converting epoch to date only string: ", epochSeconds, e)
    return ""
  }
};

/**
 * 
 * @param epochSeconds 
 * @returns 2025-11-19 2:30 PM EST
 */
const convertEpochToHumanFriendlyDateV2 = (epochSeconds: number): string => {
  try{

  
  const date = new Date(epochSeconds * 1000);
  
  // Format date and time in EST/EDT timezone
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  };
  
  const formatted = date.toLocaleString('en-US', options);
  // formatted will be like "11/19/2025, 2:30 PM EST"
  // We need to convert to "2025-11-19 2:30 PM EST"
  
  const parts = formatted.split(', ');
  if (parts.length === 2) {
    const datePart = parts[0]; // "11/19/2025"
    const timeAndTz = parts[1]; // "2:30 PM EST"
    
    const [month, day, year] = datePart.split('/');
    const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    
    return `${dateStr} ${timeAndTz}`;
  }
  
  // Fallback if formatting doesn't work as expected
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const minutesStr = String(minutes).padStart(2, '0');
  const timeStr = `${displayHours}:${minutesStr} ${ampm}`;
  
  return `${dateStr} ${timeStr} EST`;
  }catch(e){
    console.error("Error converting epoch to human friendly date: ", epochSeconds,e)
    return ""
  }
}


/**
 * 
 * @param epochSeconds epoch time in seconds
 * @returns date in the format of "MMM DD" (e.g. "Jan 01")
 */
function formatEpochToShortDate(epochSeconds: number): string {
    const date = new Date(epochSeconds * 1000);
    return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
    });
}


const convertToCorpDataObjSafely = (rawStringJson: string): CorpDataObj | null => {
  try {
    console.log("rawStringJson: ", rawStringJson);
    const parsedData = typeof rawStringJson === "string" ? JSON.parse(rawStringJson) : rawStringJson;
    
    if (!parsedData || typeof parsedData !== "object") {
      console.error("Invalid JSON structure");
      return null;
    }

    const corpData: CorpDataObj = {
      corpJobberDetails: parsedData.corpJobberDetails,
      addressDto: parsedData.addressDto,
      agencyNames: Array.isArray(parsedData.agencyNames) ? parsedData.agencyNames : [],
      iconImageS3Key: parsedData.iconImageS3Key ?? "",
      weeklyBudgetAmount: parsedData.weeklyBudgetAmount ?? 0,
      budgetNotificationThreshold: parsedData.budgetNotificationThreshold ?? 0,
      corpCustomerShiftDetails: parsedData.corpCustomerShiftDetails,
      orgLocationMapDetailsMap: new Map(
        Object.entries(parsedData.orgLocationMapDetailsMap || {}).map(([key, value]) => [key, value as OrgLocationMapDetails])
      ),
      jobberDetails: parsedData.jobberDetails ?? {},
    };

    console.log("corpData Parsed JSON: ", corpData);

    return corpData;
  } catch (error) {
    console.error("Failed to parse JSON: ", error);
    return null;
  }
};

const getOrgLocationMapDetailsV2 = ({orgData, corpName}: {orgData: CorpDataObj, corpName: string}):OrgLocationMapDetails  => {
  const locationMap = new Map(Object.entries(orgData.orgLocationMapDetailsMap || {}));
  console.log("locationMaplocationMap ", locationMap)
  const locations: OrgLocationMapDetails = locationMap.get(corpName);
  console.log("locationsKK: ", locations)
  return locations;
}

const getOrgLocationMapDetails = ({orgData, corpName}: {orgData: any, corpName: string}):OrgLocationMapDetails  => {
  if (!orgData || !corpName) return undefined;
  console.log("orgData12 ", orgData)
  
  const { orgLocationMapDetailsMap } = orgData;
  
  if (!orgLocationMapDetailsMap) return undefined;
  console.log("orgLocationMapDetailsMap ", orgLocationMapDetailsMap)
  
  // If it's a Map instance, use get method
  if (orgLocationMapDetailsMap instanceof Map) {
      const resp1= orgLocationMapDetailsMap.get(corpName);
      console.log("resp1 ", resp1)
    return resp1;
  }

  console.log("typeof ff ", typeof orgLocationMapDetailsMap)

  const res = orgLocationMapDetailsMap[corpName];
  console.log("resresresresres ", res)
  
  // Otherwise treat it as a plain object
  return res;
};


/**
 * Checks if a string is present, non-empty, and does not contain the word "undefined".
 * @param str - The string to check.
 * @returns True if the string is valid, false otherwise.
 */
const isValidString = (str: unknown): boolean => {
  // Handle null, undefined, and non-string types
  if (typeof str !== "string") {
    return false;
  }

  const trimmed = str.trim();
  
  // Check if empty after trimming
  if (trimmed === "") {
    return false;
  }

  // Check for the literal string "undefined" (case-insensitive for robustness)
  if (trimmed.toLowerCase() === "undefined") {
    return false;
  }

  return true;
};

/**
 * True when the value is a finite number (not NaN, not ±Infinity). Use before arithmetic or summing.
 */
const isValidNumber = (value: unknown): boolean => {
  try{
    if (value === null || value === undefined) {
      return false;
    }
    const asNumber: number = typeof value === "number" ? value : Number(value);
    return Number.isFinite(asNumber);
  }catch(e){
    return false;
  }
  
};

/** Delimiter used in composite org lookup keys (e.g. AgencyOne__Vancouver Care). */
const WORKY_ORG_LOOKUP_KEY_DELIMITER: string = '__';

/**
 * Returns the display label for an org: prefer a real display name override; never treat the
 * composite lookup key (e.g. AgencyOne__Vancouver Care) as a user-facing label when no override exists.
 * @param orgKey - The organization lookup key (id).
 * @param orgDetails - Org details object that may have an optional displayName (e.g. OrgLocationMapDetails).
 * @returns The display name when present and distinct from the lookup key; otherwise a friendly fallback.
 */
const getOrgDisplayLabel = (
  orgKey: string,
  orgDetails: { displayName?: string | null } | null | undefined
): string => {
  if (!isValidString(orgKey)) {
    return orgKey ?? '';
  }
  const displayName: string | null | undefined = orgDetails?.displayName;
  // Server often echoes the lookup key into displayName when no override is set — that is not a display label.
  if (isValidString(displayName) && displayName !== orgKey) {
    return displayName as string;
  }
  const delimiterIndex: number = orgKey.indexOf(WORKY_ORG_LOOKUP_KEY_DELIMITER);
  if (delimiterIndex >= 0) {
    const afterDelimiter: string = orgKey
      .slice(delimiterIndex + WORKY_ORG_LOOKUP_KEY_DELIMITER.length)
      .trim();
    if (isValidString(afterDelimiter)) {
      return afterDelimiter;
    }
  }
  return isValidString(displayName) ? (displayName as string) : orgKey;
};

const retrieveLocalStorageAttributes = (): LocalStorageObjects => {
  const attributesMap: LocalStorageObjects = {
    attributes: {}
  };

  try{
  attributesMap.attributes["worky_session_id"] = localStorage.getItem("worky_session_id");
  attributesMap.attributes[LOCAL_STORAGE_KEYS.SUB_SESSION_WORKY_ID] = localStorage.getItem(LOCAL_STORAGE_KEYS.SUB_SESSION_WORKY_ID);


  attributesMap.attributes["locationPermission"] = localStorage.getItem("locationPermission");

  // Authentication attributes
  attributesMap.attributes["auth_code"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.AUTH_CODE});
  attributesMap.attributes["access_token"] = getAccessToken();
  attributesMap.attributes["id_token"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.ID_TOKEN});
  attributesMap.attributes["refresh_token"] = getRefreshToken();
  const expireTime = getAuthTokenExpireDateTimeEpoch();
  if(expireTime){
    attributesMap.attributes["expire_time"] = expireTime.toString();
  }

  // User details
  attributesMap.attributes["userEmail"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_EMAIL});
  attributesMap.attributes["userPhoneNumber"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_PHONE_NUMBER});
  attributesMap.attributes["firstName"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.FIRST_NAME});
  attributesMap.attributes["lastName"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.LAST_NAME});
  attributesMap.attributes["userId"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_ID});

  // App permissions
  attributesMap.attributes["locationPermission"] = localStorage.getItem("locationPermission");

  // Organization details
  attributesMap.attributes["orgName"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.ORG_NAME});
  attributesMap.attributes["orgIcon"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.ORG_ICON});
  attributesMap.attributes["user_type"] = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_TYPE});


  }catch(e){
      console.log("error in retrieveLocalStorageAttributes " + JSON.stringify(attributesMap), e)
  }
  return attributesMap;
}


const getOrCreateWorkySubSessionIdToSessionStorage = (): string => {
  // Check if there already exists a session ID in local storage
  
  // sessionStorage.setItem(LOCAL_STORAGE_KEYS.SUB_SESSION_WORKY_ID, uuidv4())
  
  try{
    const existingSubSessionId = sessionStorage.getItem(LOCAL_STORAGE_KEYS.SUB_SESSION_WORKY_ID);
  
  if (existingSubSessionId) {
    return existingSubSessionId;
  }
  
  // If no session ID exists, create one using uuidv4
  const newSUBSessionId = uuidv4();
  
  try {
    // Save the new session ID to sessionStorage
    sessionStorage.setItem(LOCAL_STORAGE_KEYS.SUB_SESSION_WORKY_ID, newSUBSessionId);
    console.log("Created and saved new Worky sub-session ID:", newSUBSessionId);
  } catch (error) {
    console.error("Failed to save Worky sub-session ID to sessionStorage:", error);
    // Continue with the session ID even if storage fails
  }
  
  return newSUBSessionId;
  }catch(err){
    console.log("major error in sub-session", err)
    return "error";
  }
}
  
  /**
 * Saves or retrieves a unique session ID for the current user session
 * @returns The session ID string (either existing or newly created)
 */
const saveWorkySessionIdToLocalStorage = (): string => {
  // Check if there already exists a session ID in local storage
  
  // sessionStorage.setItem(LOCAL_STORAGE_KEYS.SUB_SESSION_WORKY_ID, uuidv4())
  
  try{
    const existingSessionId = localStorage.getItem("worky_session_id");
  
  if (existingSessionId) {
    return existingSessionId;
  }
  
  // If no session ID exists, create one using uuidv4
  const newSessionId = uuidv4();
  
  try {
    // Save the new session ID to localStorage
    localStorage.setItem("worky_session_id", newSessionId);
    console.log("Created and saved new Worky session ID:", newSessionId);
  } catch (error) {
    console.error("Failed to save Worky session ID to localStorage:", error);
    // Continue with the session ID even if storage fails
  }
  
  return newSessionId;
  }catch(err){
    console.log("major error in session", err)
    return "error";
  }
  
};

const RetrieveFromLocalCache = (cacheKey: LOCAL_STORAGE_KEYS) : string  =>{
  return localStorage.getItem(cacheKey)
}

const  toStringSafely = (obj: any): string  => {
  const seen = new WeakSet();
  try{
    // return String(obj)
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) return "[Circular]";
        seen.add(value);
      }
      return value;
    });
  }catch(e){
    console.log("error while converting to string safely, ", obj, e )
    return "[Stringify Error]"
  }
  
}

const GetAppLoadCount = () : number  => {
  const currentCount = parseInt(localStorage.getItem(LOCAL_STORAGE_KEYS.APP_LOAD_COUNT) || '0', 10) | 0;
  return currentCount;
}

const IncrementAppLoadCount = () =>{
  const currentCount = GetAppLoadCount();
  localStorage.setItem(LOCAL_STORAGE_KEYS.APP_LOAD_COUNT, (currentCount + 1).toString());
}

const HasDirectPitchPopUpAlreadyBeenDisplayed = () : boolean => {
  const popUpDirectPitchCount = parseInt(RetrieveFromLocalCache(LOCAL_STORAGE_KEYS.POP_UP_DIRECT_PITCH_COUNT) || '0', 10) | 0;

  return popUpDirectPitchCount >= 2;
}

const DetermineIfCanLoadDirectPitch = ():boolean =>{
  const isDirectPitchPopUpAlreadyBeenDisplayed :boolean = HasDirectPitchPopUpAlreadyBeenDisplayed();
  if(isDirectPitchPopUpAlreadyBeenDisplayed){
    return false;
  }

  return GetAppLoadCount() <= 5;
}

const IncrementPitchPopUpCount = () => {
  const currentCount = parseInt(localStorage.getItem('pop_up_direct_pitch_count') || '0', 10);
  localStorage.setItem('pop_up_direct_pitch_count', (currentCount + 10).toString());
}


/**
 * Safely retrieves and parses the organization data from localStorage
 * @returns The parsed organization data or null if not available/invalid
 */
const getLocalStorageOrgData = (): CorpDataObjSimpleFromLocalStorage | null => {
  try {
    // Get the raw string from localStorage
    const orgDataString = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.ORG_DATA});
    
    // Check if data exists
    if (!orgDataString) {
      console.log("No organization data found in localStorage");
      return null;
    }
    
    // Attempt to parse the JSON
    const orgMetaData: CorpDataObjSimpleFromLocalStorage = JSON.parse(orgDataString);
    
    // Validate the parsed data
    if (!orgMetaData || typeof orgMetaData !== 'object') {
      console.warn("Invalid organization data format in localStorage");
      return null;
    }
    
    return orgMetaData;
  } catch (error) {
    console.error("Failed to retrieve or parse organization data from localStorage: ", localStorage.getItem('orgData'), error);
    return null;
  }
};


const rawStringCharsEquals = (s1: string | null | undefined, s2: string | null | undefined): boolean => {
  // Check if both strings are not present (null, undefined, or empty)
  if (!s1 && !s2) return true;

  // Remove whitespace and convert to lowercase
  const clearedS1 = removeAllWhiteSpaceAndToLowerCase(s1);
  const clearedS2 = removeAllWhiteSpaceAndToLowerCase(s2);

  // Check if cleared strings are not present
  if (!clearedS1 && !clearedS2) return true;

  // Check for null/undefined mismatches
  if (!clearedS1 && clearedS2) return false;
  if (clearedS1 && !clearedS2) return false;

  // Compare strings case-insensitively in both directions
  return clearedS1.toLowerCase() === clearedS2.toLowerCase() && 
         clearedS2.toLowerCase() === clearedS1.toLowerCase();
}

// Helper function to remove whitespace and convert to lowercase
const removeAllWhiteSpaceAndToLowerCase = (input?: string | null): string => {
  return input ? input.replace(/\s/g, '').toLowerCase() : '';
}

const findMapKeyByValue = (
  map: Map<string, OrgLocationMapDetails>,
  searchValue: string | null 
): OrgLocationMapDetails  => {
  // If search value is empty or null, return undefined
  if (!searchValue) return undefined;

  // Iterate through map entries
  for (const [key, value] of map.entries()) {
      // Convert value to string, handling different types
      const stringValue = value !== null && value !== undefined
          ? String(value).trim()
          : '';

      // Use our previously created rawStringCharsEquals method to compare
      if (rawStringCharsEquals(key, searchValue)) {
          return value;
      }
  }

  return {
    orgLocationMap: new Map<string, OrgLocation>()
  };
}

interface OrgLocationMapEntry {
  readonly locationName: string;
  readonly location: OrgLocation;
}

const getOrgLocationEntriesFromMap = (
  locations: Map<string, OrgLocation> | Record<string, OrgLocation> | null | undefined
): readonly OrgLocationMapEntry[] => {
  if (!locations) {
    return [];
  }

  if (locations instanceof Map) {
    const entries: OrgLocationMapEntry[] = [];
    locations.forEach((location: OrgLocation, locationName: string) => {
      if (location) {
        entries.push({ locationName, location });
      }
    });
    return entries;
  }

  return Object.entries(locations)
    .filter((entry): entry is [string, OrgLocation] => Boolean(entry[1]))
    .map(([locationName, location]) => ({
      locationName,
      location,
    }));
};

const getSingleOrgLocationIfOnlyOne = (
  locations: Map<string, OrgLocation> | Record<string, OrgLocation> | null | undefined
): OrgLocation | null => {
  const locationEntries: readonly OrgLocationMapEntry[] = getOrgLocationEntriesFromMap(locations);
  if (locationEntries.length !== 1) {
    return null;
  }
  return locationEntries[0].location;
};



const roundToTwoDecimals = (amount: number): number => {
  // Use Math.round with a multiplier to handle floating point precision issues
  return Math.round((amount + Number.EPSILON) * 100) / 100;
};

/**
 * Returns a monetary amount rounded to two decimal places (e.g. 12.34).
 * Returns 0 when the value is null, undefined, or not a finite number.
 */
const generateSafeHumanFriendlyMonetaryNumber = (number: unknown): number => {
  if (!isValidNumber(number)) {
    return 0;
  }
  const numericValue: number = typeof number === "number" ? number : Number(number);
  return roundToTwoDecimals(numericValue);
};

const toUpperCase = (str: string): string => {
  return str ? str.toUpperCase() : '';
};

/**
 * Removes all whitespace (leading, trailing, and within) and special characters from a string, converting to lowercase
 * @param input - The input string to process
 * @returns The cleaned string with only alphanumeric characters in lowercase, or empty string if input is null/undefined
 */
const cleanStringForComparison = (input?: string | null): string => {
  try {
    if (!input || typeof input !== 'string') {
      return '';
    }
    
    // Remove all whitespace (spaces, tabs, newlines, etc.) and special characters
    // Keep only alphanumeric characters (a-z, A-Z, 0-9)
    return input.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  } catch (error) {
    console.error('Error in cleanStringForComparison:', { input, error });
    return '';
  }
};

/**
 * Formats a website URL to ensure it's clickable and properly formatted
 * @param website - The website string (e.g., "kiewit.com", "www.example.com", "https://example.com")
 * @returns A properly formatted URL with protocol, or empty string if invalid
 */
const formatWebsiteUrl = (website?: string | null): string => {
  try {
    if (!website || typeof website !== 'string') {
      return '';
    }
    
    // Trim whitespace
    const trimmedWebsite = website.trim();
    
    if (!trimmedWebsite) {
      return '';
    }
    
    // If it already has a protocol, return as is
    if (trimmedWebsite.startsWith('http://') || trimmedWebsite.startsWith('https://')) {
      return trimmedWebsite;
    }
    
    // If it starts with www., add https://
    if (trimmedWebsite.startsWith('www.')) {
      return `https://${trimmedWebsite}`;
    }
    
    // For domains like "kiewit.com", add https://
    // Basic domain validation: contains at least one dot and valid characters
    const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/;
    if (domainPattern.test(trimmedWebsite) && trimmedWebsite.includes('.')) {
      return `https://${trimmedWebsite}`;
    }
    
    // If it doesn't match expected patterns, return empty string
    return '';
  } catch (error) {
    console.error('Error in formatWebsiteUrl:', { website, error });
    return '';
  }
};

/**
 * Formats a revenue amount into a human-readable format (e.g., 6811875 -> "6.8M")
 * @param revenueAmount - The revenue amount as a string or number
 * @returns A formatted string with K (thousands), M (millions), or B (billions) suffix, or empty string if invalid
 */
const formatRevenueAmount = (revenueAmount?: string | number | null): string => {
  try {
    if (revenueAmount === null || revenueAmount === undefined) {
      return '';
    }

    // Convert to number if it's a string
    const numValue: number = typeof revenueAmount === 'string' 
      ? parseFloat(revenueAmount) 
      : revenueAmount;

    // Check if the parsed value is a valid number
    if (isNaN(numValue) || numValue < 0) {
      return '';
    }

    // Handle billions
    if (numValue >= 1000000000) {
      const billions: number = numValue / 1000000000;
      // Round to 1 decimal place
      const rounded: number = Math.round(billions * 10) / 10;
      return `${rounded}B`;
    }

    // Handle millions
    if (numValue >= 1000000) {
      const millions: number = numValue / 1000000;
      // Round to 1 decimal place
      const rounded: number = Math.round(millions * 10) / 10;
      return `${rounded}M`;
    }

    // Handle thousands
    if (numValue >= 1000) {
      const thousands: number = numValue / 1000;
      // Round to 1 decimal place
      const rounded: number = Math.round(thousands * 10) / 10;
      return `${rounded}K`;
    }

    // For values less than 1000, return as is
    return numValue.toString();
  } catch (error) {
    console.error('Error in formatRevenueAmount:', { revenueAmount, error });
    return '';
  }
};



const clearAuthTokens = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.EXPIRE_TIME);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_ID);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_TYPE);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ORG_NAME);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ORG_ICON);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_EMAIL);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_PHONE_NUMBER);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.FIRST_NAME);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_NAME);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.EXPIRE_TIME);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.USER_ID);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.USER_TYPE);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.ORG_NAME);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.ORG_ICON);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.USER_EMAIL);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.USER_PHONE_NUMBER);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.FIRST_NAME);
  sessionStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_NAME);
}

const getAccessToken = () => {
  return sessionStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
}
const getRefreshToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
}

const setAccessToken = (accessToken: string) => {
  sessionStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
}
const setRefreshToken = (refreshToken: string) => {  
  localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
}

const getAuthFlow = (): string | null => {
  const localStorageAuthFlow: string | null = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_FLOW);
  if (isValidString(localStorageAuthFlow)) {
    return localStorageAuthFlow;
  }

  return sessionStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_FLOW);
}

const setAuthFlow = (authFlow: string): void => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_FLOW, authFlow);
  sessionStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_FLOW, authFlow);
}

const getAuthTokenExpireDateTimeEpoch = (): number => {
  const expireTime = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPIRE_TIME);  
  if(!expireTime || isNaN(parseInt(expireTime))){
    return null;
  }
  return parseInt(expireTime);
}
const setSessionStorageValue = ({key, value}: {key: LOCAL_STORAGE_KEYS, value: string}) => {
  sessionStorage.setItem(key, value);
}
const getSessionStorageValue = ({key}: {key: LOCAL_STORAGE_KEYS}): string => {
  return sessionStorage.getItem(key);
}


const setLocalStorageValue = ({key, value}: {key: LOCAL_STORAGE_KEYS, value: string}) => {
  localStorage.setItem(key, value);
}

const handleSignOut = (): void => {
  void import('../auth/WorkyAuthSignOutService').then(({ performWorkySignOut }) => {
    void performWorkySignOut();
  });
}

const clearAllSessionValues = () => {
  // Clear all session storage values using the LOCAL_STORAGE_KEYS enum
  try{
    Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key);
    });
  
    Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
      if (
        key !== LOCAL_STORAGE_KEYS.WORKY_SESSION_ID &&
        key !== LOCAL_STORAGE_KEYS.COOKIE_CONSENT_STATUS
      ) {
        localStorage.removeItem(key);
      }
    });

  }catch(e){
    console.error("Error clearing all session values: ", e);
  }
}

const setAuthTokenExpireDateTimeEpoch = (expireTime: number) => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.EXPIRE_TIME, expireTime.toString());
}

/**
 * Returns a helpful message based on the JobberStatus.
 * @param status - The JobberStatus to check.
 * @returns A descriptive string message for the status.
 */
const getJobberStatusMessage = (status: JobberStatus | string | undefined | null): string => {
  if (!status) {
      return "This worker has not completed their onboarding. They are currently in progress";
  }

  switch (status) {
      case JobberStatus.DROPPED:
      case JobberStatus.CANCELLED:
          return "Deactivate and offboard the worker.";
      case JobberStatus.FULLY_HIRED:
          return "Hired workers, allowing them to be assigned to shifts";
      case JobberStatus.REGISTERED_BY_ORG_AGENCY_NEEDS_TO_ON_BOARD:
          return "Worker has been invited and is yet to login and activate their account";
      default:
          return "This worker has not completed their onboarding. They are currently in progress";
  }
}

const getJobberStatusMessageForReadOnly = (status: JobberStatus | string | undefined | null): string => {
  if (!status) {
      return "Not Set";
  }

  switch (status) {
      case JobberStatus.DROPPED:
      case JobberStatus.CANCELLED:
          return "Deactivated and offboarded workers";
      case JobberStatus.FULLY_HIRED:
          return "Hired workers";
      case JobberStatus.REGISTERED_BY_ORG_AGENCY_NEEDS_TO_ON_BOARD:
          return "Invited workers";
      case JobberStatus.REGISTERED_ON_COGNITO_COMPLETED:
        return "Registration in progress";
      case JobberStatus.REGISTERED_NEEDS_TO_COMPLETE_ONBOARDING_TEMPLATE:
        return "Onboarding in progress";
      case JobberStatus.REGISTERED_SUBMITTED_JOBBER_REG_FORM:
          return "Registration complete";
      default:
          return "Not Set";
  }
}


const getJobberStatusMessageForReadOnlySingleWorker = (status: JobberStatus | string | undefined | null): string => {
  if (!status) {
      return "Not Set";
  }

  switch (status) {
      case JobberStatus.DROPPED:
      case JobberStatus.CANCELLED:
          return "Deactivated and offboarded";
      case JobberStatus.FULLY_HIRED:
          return "Hired";
      case JobberStatus.REGISTERED_ON_COGNITO_COMPLETED:
        return "Registration in progress";
      case JobberStatus.REGISTERED_NEEDS_TO_COMPLETE_ONBOARDING_TEMPLATE:
        return "Onboarding in progress";
      case JobberStatus.REGISTERED_SUBMITTED_JOBBER_REG_FORM:
          return "Registration complete"
      case JobberStatus.REGISTERED_BY_ORG_AGENCY_NEEDS_TO_ON_BOARD:
          return "Invited";
      default:
          return "Pending";
  }
}

/**
 * Returns a CSS class name for styling jobber status badges with status-appropriate colors.
 * Use with wgc-status-badge as base class: className={`wgc-status-badge ${getJobberStatusDisplayClassName(status)}`}
 * @param status - The JobberStatus to style.
 * @returns A status-specific className for background and text color (e.g. wgc-status-hired).
 */
const getJobberStatusDisplayClassName = (status: JobberStatus | string | undefined | null): string => {
  if (!status) {
      return "wgc-status-pending";
  }

  switch (status) {
      case JobberStatus.FULLY_HIRED:
          return "wgc-status-hired";
      case JobberStatus.DROPPED:
      case JobberStatus.CANCELLED:
          return "wgc-status-deactivated";
      case JobberStatus.REGISTERED_BY_ORG_AGENCY_NEEDS_TO_ON_BOARD:
          return "wgc-status-invited";
      case JobberStatus.REGISTERED_ON_COGNITO_COMPLETED:
      case JobberStatus.REGISTERED_NEEDS_TO_COMPLETE_ONBOARDING_TEMPLATE:
          return "wgc-status-registration-in-progress";
      case JobberStatus.REGISTERED_SUBMITTED_JOBBER_REG_FORM:
          return "wgc-status-registration-complete";
      default:
          return "wgc-status-pending";
  }
}

const isJobberActiveAndHired = (status: JobberStatus ): boolean => {
  if (!status) {
    return false;
  }
  return status === JobberStatus.FULLY_HIRED;
}



/**
 * Client-facing label for persisted NOT_PROVIDED placeholder values.
 */
const WORKY_NOT_PROVIDED_FIELD_VALUE: string = 'NOT_PROVIDED';
const WORKY_NOT_PROVIDED_DISPLAY_LABEL: string = 'Not Provided';
const WORKY_YET_TO_REGISTER_DISPLAY_LABEL: string = 'Yet to register';

const getFriendlyFieldDisplayValue = (value: string | null | undefined): string => {
  if (!isValidString(value) || value === WORKY_NOT_PROVIDED_FIELD_VALUE) {
    return WORKY_NOT_PROVIDED_DISPLAY_LABEL;
  }
  return value;
};

const getUserEmailDisplayValue = (
  userEmail: { readonly value?: string | null } | null | undefined
): string => {
  return userEmail?.value ?? '';
};

const isYetToRegisterUser = (
  firstName: string | null | undefined,
  lastName: string | null | undefined
): boolean => {
  return (
    firstName === WORKY_NOT_PROVIDED_FIELD_VALUE
    && lastName === WORKY_NOT_PROVIDED_FIELD_VALUE
  );
};

const getFriendlyUserDisplayName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string => {
  const normalizedFirstName: string = isValidString(firstName) ? firstName : '';
  const normalizedLastName: string = isValidString(lastName) ? lastName : '';

  if (
    normalizedFirstName === WORKY_NOT_PROVIDED_FIELD_VALUE
    && normalizedLastName === WORKY_NOT_PROVIDED_FIELD_VALUE
  ) {
    return WORKY_NOT_PROVIDED_DISPLAY_LABEL;
  }
  if (normalizedFirstName === WORKY_NOT_PROVIDED_FIELD_VALUE) {
    return getFriendlyFieldDisplayValue(normalizedLastName);
  }
  if (normalizedLastName === WORKY_NOT_PROVIDED_FIELD_VALUE) {
    return getFriendlyFieldDisplayValue(normalizedFirstName);
  }

  const combinedName: string = [normalizedFirstName, normalizedLastName]
    .filter((namePart: string) => isValidString(namePart))
    .join(' ');
  return isValidString(combinedName) ? combinedName : WORKY_NOT_PROVIDED_DISPLAY_LABEL;
};

/**
 * Checks if a phone number matches the test/placeholder phone number.
 * Compares both the original format and a normalized version (digits only).
 * Matches +19056087750 and 9056087750 (with or without leading country code 1).
 * @param phoneNumber - The phone number to check.
 * @returns True if the phone number matches the test number, false otherwise.
 */
const isTestPhoneNumber = (phoneNumber: string | null | undefined): boolean => {
  const TEST_PHONE_NUMBER = '+19056087750';
  const TEST_PHONE_DIGITS_WITHOUT_COUNTRY_CODE = '9056087750';
  
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false;
  }

  // Exact match check
  if (phoneNumber === TEST_PHONE_NUMBER) {
    return true;
  }

  // Normalize both numbers by removing all non-digit characters
  const normalizePhoneNumber = (phone: string): string => {
    return phone.replace(/\D/g, '');
  };

  const normalizedInput = normalizePhoneNumber(phoneNumber);
  const normalizedTest = normalizePhoneNumber(TEST_PHONE_NUMBER);

  if (normalizedInput === normalizedTest) {
    return true;
  }
  if (normalizedInput === TEST_PHONE_DIGITS_WITHOUT_COUNTRY_CODE) {
    return true;
  }
  return false;
};

const formatPhoneNumberForDisplay = (phoneNumber: string | null | undefined): string => {
  if (!isValidString(phoneNumber) || phoneNumber === undefined || phoneNumber === null) {
    return '';
  }
  const digitsOnly: string = phoneNumber.replace(/\D/g, '');
  let nationalDigits: string = digitsOnly;
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    nationalDigits = digitsOnly.slice(1);
  }
  if (nationalDigits.length !== 10) {
    return phoneNumber;
  }
  const areaCode: string = nationalDigits.slice(0, 3);
  const exchangeCode: string = nationalDigits.slice(3, 6);
  const subscriberNumber: string = nationalDigits.slice(6, 10);
  return `(${areaCode})-${exchangeCode}-${subscriberNumber}`;
};

const getFriendlyPhoneDisplayValue = (phoneNumber: string | null | undefined): string => {
  if (isTestPhoneNumber(phoneNumber)) {
    return WORKY_NOT_PROVIDED_DISPLAY_LABEL;
  }
  return getFriendlyFieldDisplayValue(formatPhoneNumberForDisplay(phoneNumber));
};


export { isValidString, isValidNumber, getOrgDisplayLabel, formatEpochToShortDate, convertEpochToHumanFriendlyDate, convertEpochToDateOnlyString, convertEpochToHumanFriendlyDateV2, extractRatingFromRatingCategory, CopyToClipboard, ExtractStarRating, JobberStarRating, JobberViewRating, DetermineUserType,
  convertToCorpDataObjSafely,
  getOrgLocationMapDetails,
  getOrgLocationMapDetailsV2,
  retrieveLocalStorageAttributes,
  saveWorkySessionIdToLocalStorage,
  toStringSafely,
  getLocalStorageOrgData,
  findMapKeyByValue,
  getOrgLocationEntriesFromMap,
  getSingleOrgLocationIfOnlyOne,
  roundToTwoDecimals,
  generateSafeHumanFriendlyMonetaryNumber,
  toUpperCase,
  cleanStringForComparison,
  formatWebsiteUrl,
  formatRevenueAmount,
  rawStringCharsEquals,
  GetAppLoadCount,
  IncrementAppLoadCount,
  DetermineIfCanLoadDirectPitch,
  IncrementPitchPopUpCount,
  RetrieveFromLocalCache,
  LOCAL_STORAGE_KEYS,
  getOrCreateWorkySubSessionIdToSessionStorage,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  getAuthFlow,
  setAuthFlow,
  getAuthTokenExpireDateTimeEpoch,
  clearAuthTokens, 
  setSessionStorageValue,
  setLocalStorageValue,
  handleSignOut,
  getSessionStorageValue,
  clearAllSessionValues,
  setAuthTokenExpireDateTimeEpoch,
  getJobberStatusMessage,
  getJobberStatusMessageForReadOnly,
  getJobberStatusMessageForReadOnlySingleWorker,
  getJobberStatusDisplayClassName,
  isJobberActiveAndHired,
  isTestPhoneNumber,
  getFriendlyFieldDisplayValue,
  getFriendlyUserDisplayName,
  formatPhoneNumberForDisplay,
  getFriendlyPhoneDisplayValue,
  getUserEmailDisplayValue,
  isYetToRegisterUser,
  WORKY_NOT_PROVIDED_FIELD_VALUE,
  WORKY_YET_TO_REGISTER_DISPLAY_LABEL,
};
